import React, { useEffect, useState } from 'react';
import { PageHeader } from '../../../constants/style/common.styled';
import Heading from '../../../components/typography';
import {
  DynamicTable,
  withDynamicTableContext,
  useDynamicTable,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import { SharePoolManagementHeader } from '../../../constants/table-headers/SharePoolManagementHeader';
import Spinners from '../../../components/spinner';
import { useMousePositionAsTrigger } from 'react-laag';
import { ModalStatusType } from '../../../types/common';
import {
  useAdvanceBoolean,
  useBoolean,
  useGetSharePoolList,
  useGetTablePreferences,
  usePostTablePreferences,
  useWealthlaneAwardApi,
} from '../../../hooks';
import AlertPopup from '../../../components/alert-popup';
import { PoolService } from '../../../services/wealthlane-award-services';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import { useQueryClient } from 'react-query';
import Button from '../../../components/button';
import { axiosInstance } from '../../../services/apiInterceptor';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import SharePoolModal from './sharePoolModal';
import InitialValuesForm from '../../../constants/initialValues';

const initialValues = {
  data: InitialValuesForm.sharePoolManagement,
  id: '',
};

const SharePoolManagement = () => {
  const { handleRequest, error: exception } = useWealthlaneAwardApi();
  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const [modalLoading, setModalLoading] = useState(false);
  const queryClient = useQueryClient();
  const [headerList, setHeaderList] = useState<string[]>([]);
  const [initialData, setInitialData] = useState(initialValues);
  const [rowData, setRowData] = useState();
  const { tableInfo, setTableInfo, registerPermissionHandler } = useDynamicTable();
  const { data, status } = useGetSharePoolList(handleRequest, tableInfo);
  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
    customValue,
    setCustomValue,
  } = useAdvanceBoolean<string>(false);

  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'SHARE_POOL_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });
  const { data: tablePreferences } = useGetTablePreferences(handleRequest, 'Share Pool');

  useEffect(() => {
    tablePreferences != null && setHeaderList([...tablePreferences]);
  }, [tablePreferences]);

  const { mutate: postTablePreference, isLoading: tablePrefLoading } =
    usePostTablePreferences(handleRequest);

  const handleSavePreference = (value: string[]): void => {
    const prefPayload = {
      tableName: 'Share Pool',
      preferences: value,
    };
    postTablePreference(prefPayload);
  };

  const tableActions = [
    {
      action: 'View',
      id: 'view',
      onClick: (id: string, rowData: any) => {
        setModalStatus('View');
        setRowData(rowData);
        openModal();
      },
    },
    {
      action: 'Edit',
      id: 'edit',
      as: 'Edit',
      onClick: (id: string, rowData: any) => {
        setModalStatus('Edit');
        setRowData(rowData);
        openModal();
      },
    },
    {
      action: <div className='delete-label'>Delete</div>,
      id: 'delete',
      as: 'Delete',
      onClick: (id: string) => {
        setModalStatus('Delete');
        openConfirmModal();
        setCustomValue(id);
      },
    },
  ];

  const {
    hasMousePosition, // did we get a mouse-position from the event-handler
    resetMousePosition, // reset the mouse-position to `null`, essentially closing the menu
    handleMouseEvent, // event-handler we will use below
    trigger, // information regarding positioning we can provide to `useLayer`
  } = useMousePositionAsTrigger();

  const renderRightToolbarButton = () => {
    return (
      <Button
        id='addButton'
        as='Create'
        variant={'primary'}
        title={'Add Share Pool'}
        onClick={() => {
          setRowData(null);
          setModalStatus('Add');
          openModal();
        }}
        icon='add_circle'
      />
    );
  };

  const tableHeaderList = SharePoolManagementHeader({
    actions: schemaData?.columns,
    tableActions,
    hasMousePosition,
    resetMousePosition,
  })
    .filter((item) => item.isSubHeader)
    .map((item) => {
      // console.log('item', item);

      return {
        label: item.Header,
        value: item.accessor,
      };
    });
  const sharePoolManagementHeader = (): any[] => {
    return SharePoolManagementHeader({
      actions: schemaData?.columns,
      tableActions,
      hasMousePosition,
      resetMousePosition,
    }).filter((item) => item.isSubHeader === false || headerList.includes(item.accessor));
  };
  useEffect(() => {
    if (exception != null) {
      closeModal();
    }
  }, [exception]);
  const schema = React.useMemo(
    () => ({
      columns: sharePoolManagementHeader(),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );
  const onPrimaryClick = (values: Record<string, any>): void => {
    deleteSharePoolById(customValue);
  };

  const deleteSharePoolById = (id: string): void => {
    setModalLoading(true);
    handleRequest(PoolService.deleteApiAwardApiAppPoolPool(id))
      .then((data: any) => {
        if (data != null) {
          toast.success(<ToastComponent label={'Success'} message={'Share Pool Deleted.'} />);
          void queryClient.invalidateQueries('getSharePoolList');
          setModalLoading(false);
          closeConfirmModal();
        }
      })
      .catch(() => {
        setModalLoading(false);
        closeConfirmModal();
      })
      .finally(() => {
        setModalLoading(false);
        closeConfirmModal();
      });
  };

  if (isTableSchemaLoading) return <Spinners id='loadingSpinner' containerClassName='h-screen' />;
  return (
    <div data-page='sharePoolManagement' id='pageContainer'>
      <div id='pageHeader'>
        <PageHeader>
          <Heading id='pageTitle' variant='h3' title='Share Pool Management' />
          <Heading id='pageSubtitle' variant='body-s' title='Current list of share pools.' />
        </PageHeader>
      </div>

      <DynamicTable
        id='sharePoolTable'
        schema={schema}
        data={data?.items ?? []}
        totalCounts={data?.totalCount ?? 10}
        loading={status}
        components={{
          renderRightToolbarButton,
        }}
        tableColumnList={tableHeaderList}
        selectedColumnList={headerList}
        tablePreferenceLoading={tablePrefLoading}
        handleColumnSave={handleSavePreference}
      />
      {isConfirmOpen && (
        <AlertPopup
          id='deleteConfirmationModal'
          isShow={isConfirmOpen}
          closeModal={closeConfirmModal}
          modalStatus={modalStatus}
          title={'this Share Pool'}
          handleSubmit={onPrimaryClick}
          // setRemark={setRemark}
          // remark={remark}
          loading={modalLoading}
        />
      )}
      <AnimationWrapper id='modalAnimationWrapper'>
        {isModalOpen && (
          <SharePoolModal
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            modalStatus={modalStatus}
            initialData={initialData}
            setModalStatus={setModalStatus}
            rowData={rowData}
          />
        )}
      </AnimationWrapper>
    </div>
  );
};
export default withDynamicTableContext(SharePoolManagement, false, axiosInstance);
